import { wcagContrastUnrounded } from "../../services/wcag-contrast";
import { PALETTE_STEPS } from "../../tokens/palette";
import { applyResolver, assetUrl, isBlock, list, text } from "../../services/fields";

/* The shared CMS-field coercions live in `services/fields.ts` (WAT-12451); re-exported
   from the old path so the client modules importing them here do not change. ⚠ Three
   client modules import this file, so what it reaches ships to the browser (WAT-12200). */
export { applyResolver, assetUrl, isBlock, list, text };

/* Anything but a hex colour or a palette-step-shaped name is refused. Dev also
   refuses an unpublished step and production does not; the outcome matches
   either way, because the property is registered `inherits: true`. */
const HEX_COLOUR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/;
const PALETTE_STEP = /^[a-z]+-\d{2,3}$/;

/** The colour as a CSS value, or undefined to emit nothing — the property then
 *  takes its INHERITED value. FORMAT ONLY: it reads `title_color` and
 *  `accent_deep` too, over which the rail's ring never sits, so the legibility
 *  refusal is paintBackdrop()'s alone (ring-legibility.server.ts). */
export function paint(value: unknown): string | undefined {
  const raw = text(value).toLowerCase();

  if (HEX_COLOUR.test(raw)) {
    return raw;
  }

  if (PALETTE_STEP.test(raw)) {
    const published = process.env.NODE_ENV === "production" || PALETTE_STEPS.has(`--${raw}`);

    return published ? `var(--${raw})` : undefined;
  }

  return undefined;
}

/**
 * ⚠ Dynamic text reaches the section and its cards ALREADY RESOLVED by the
 * Dynamic Pricing Injection Service. A string still carrying `[<TOKEN>]`
 * therefore never resolved, and showing it to a customer is worse than
 * showing nothing. Shared by both producers so a regex widened on one side
 * cannot leave a raw placeholder reachable on the other.
 */
const UNRESOLVED_TOKEN = /\[<[\s\S]*?>\]/;

/**
 * The field's content, or "" when absent, blank, or still holding an
 * unresolved token. `unresolved` accumulates every refused field's name,
 * unconditionally — a caller sharing one array across multiple cards (the
 * section's `cardLabel` sweep) dedupes at the point it prints the message,
 * not here, so this stays the one shape both producers call.
 */
export function content(value: unknown, field: string, unresolved: string[]): string {
  const trimmed = text(value);

  if (trimmed === "") {
    return "";
  }

  if (UNRESOLVED_TOKEN.test(trimmed)) {
    unresolved.push(field);
    return "";
  }

  return trimmed;
}

export const AA_NORMAL = 4.5;

/** What the accent wash composites over, and the surface both this module's
 *  title arithmetic and the server-only ring arithmetic measure against. */
export const CARD_FILL = "--slate-950";

/* The wash's opening stop, from the radial-gradient in grow-card.module.css.
   Pinned by grow-card.spec.tsx, which reads the stop out of that stylesheet. */
export const WASH_PEAK = 0.6;

/* expand, opaque, blend and accentWash are exported for
   ring-legibility.server.ts, which composites the same wash — duplicating them
   there would let the two composites drift apart, and titleContrast reaches all
   four. */

/** `#rgb`/`#rgba` widened to `#rrggbb`/`#rrggbbaa`; longer forms unchanged. */
export function expand(raw: string): string {
  const body = raw.slice(1);

  return body.length <= 4 ? `#${body.replace(/./g, channel => channel + channel)}` : raw;
}

/** A six-digit hex to take a ratio against — an alpha hex has none of its own. */
export function opaque(value: unknown): string | undefined {
  const raw = text(value).toLowerCase();

  if (/^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/.test(raw)) {
    return expand(raw);
  }

  return PALETTE_STEP.test(raw) ? PALETTE_STEPS.get(`--${raw}`) : undefined;
}

export function blend(over: string, under: string, ratio: number): string {
  const channel = (hex: string, at: number) => parseInt(hex.slice(at, at + 2), 16);
  const mix = (at: number) => {
    const value = channel(over, at) * ratio + channel(under, at) * (1 - ratio);

    return Math.round(value).toString(16).padStart(2, "0");
  };

  return `#${mix(1)}${mix(3)}${mix(5)}`;
}

/**
 * The accent wash as it ACTUALLY paints over `under`, at the gradient opening
 * stop. An alpha hex has no ratio of its own, so the alpha is folded into the
 * wash's ratio rather than dropped: `#f0ab` composites at 73% of WASH_PEAK and
 * is RATED, not declined. Undefined where there is nothing to composite —
 * unauthored, not a colour at all, or a step the token layer does not publish.
 * ⚠ NOT A VALIDATOR, and not claiming to be one: `paint()` is the gate and it
 * runs FIRST at the only call site, so a shape paint() refuses but this one
 * happens to slice (`#0123456z` → solid `#012345`, alpha `0x6`) is rated
 * rather than refused. ringRatios() has the identical hole, for the identical
 * reason — paintBackdrop() gates it.
 *
 * ⚠ ringRatios() in ring-legibility.server.ts still computes this composite
 * INLINE, with its own copy of the same three lines. Collapsing it onto this
 * one is a one-line follow-up, and until it lands the two have to move
 * together — the drift the note above warns about (WAT-12200).
 */
export function accentWash(value: unknown, under: string): string | undefined {
  const raw = text(value).toLowerCase();
  /* Only a hex widens — expand() would mangle a short step name. */
  const full = raw.startsWith("#") ? expand(raw) : raw;
  /* `green-600` is nine characters too, so the `#` is what decides. */
  const translucent = full.startsWith("#") && full.length === 9;
  const solid = opaque(translucent ? full.slice(0, 7) : full);

  if (solid === undefined) {
    return undefined;
  }

  const alpha = translucent ? parseInt(full.slice(7, 9), 16) / 255 : 1;

  return blend(solid, under, WASH_PEAK * alpha);
}

/* UNROUNDED, for the reason ring-legibility.server.ts takes the same
   reading: wcagContrast rounds to 2dp, which admits a true 4.4951 into a
   >= AA_NORMAL check. What reads this number compares it against that
   threshold and rounds only to PRINT it (grow-card.tsx). */
function contrast(fg: string, bg: string): number | null {
  return process.env.NODE_ENV === "production" ? null : wcagContrastUnrounded(fg, bg);
}

/* The title is rated at BOTH bounds — the bare card fill at one end, the
   accent at the wash's peak at the other — and the worse of the two stands.
   Undefined when either colour has no ratio of its own. WAT-12401 made the
   FILL leg a BOUND rather than a position: at the shipped `rx: 130%` the wash
   ramps to transparent only AT the card's edge, so no point inside the card
   sits on bare CARD_FILL and the real background is always between the two
   legs. (At the matrix-implied ~105% the bottom-left fell past the last stop
   and the fill leg WAS a real position — the claim moves with the radius.)
   ⚠ TWO INDEPENDENT LEGS, and only one of them reads `accent`. The FILL
   leg measures against CARD_FILL, which every card paints whatever its accent
   does, so it is always COMPUTABLE (which is the claim here — not that any
   point on the card still SITS on it; see above). The WASH leg is the one that needs an
   accent, and `accentWash()` RATES a translucent one rather than declining it,
   so nothing but `measureWash: false` drops that leg. Pass `false` exactly
   where `paint(accent) === undefined` — the card publishes no accent there, and
   the wash leg would return undefined and take the fill reading down with it.
   Gating the WHOLE reading would suppress the fill leg the same way, and a
   title that fails AA against CARD_FILL fails it on every card (WAT-12200). */
export function titleContrast(
  titleColor: unknown,
  accent: unknown,
  measureWash = true
): number | undefined {
  const title = opaque(titleColor);
  const fill = PALETTE_STEPS.get(CARD_FILL);

  if (title === undefined || fill === undefined) {
    return undefined;
  }

  const onFill = contrast(title, fill);

  if (onFill === null) {
    return undefined;
  }

  if (!measureWash) {
    return onFill;
  }

  /* NO FALLBACK. A colour this cannot resolve is the caller's to gate with
     `measureWash: false`; substituting one put a number on a backdrop nothing
     paints (WAT-12200). */
  const wash = accentWash(accent, fill);
  const onWash = wash === undefined ? null : contrast(title, wash);

  if (onWash === null) {
    return undefined;
  }

  return Math.min(onFill, onWash);
}
